import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  writeBatch,
  runTransaction
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  AthleteProfile,
  PickupGame,
  CourtPOI,
  ChatMessage,
  Tournament,
  SportType,
  UserLevel,
  AppNotification,
  AuthProviderSettings,
  EmailDeliveryLog,
  ActivityFeedItem
} from '../types';

// Firestore collection names
const ATHLETES_COL = 'athletes';
const GAMES_COL = 'pickupGames';
const MESSAGES_COL = 'messages';
const COURTS_COL = 'courts';
const TOURNAMENTS_COL = 'tournaments';
const NOTIFICATIONS_COL = 'notifications';
const HIGHLIGHTS_COL = 'highlights';
const PRESENCE_COL = 'presence';
const SETTINGS_COL = 'settings';
const FEED_COL = 'activityFeed';

export interface PresenceDoc {
  userId: string;
  isOnline: boolean;
  status?: 'Online' | 'In-Game' | 'Away';
  isTyping?: boolean;
  typingTargetId?: string;
  lastActive?: number | string;
}

export function subscribePresence(onUpdate: (presenceMap: Record<string, PresenceDoc>) => void) {
  return onSnapshot(collection(db, PRESENCE_COL), (snapshot) => {
    const map: Record<string, PresenceDoc> = {};
    snapshot.forEach((docSnap) => {
      const data = docSnap.data() as PresenceDoc;
      if (data && data.userId) {
        map[data.userId] = data;
      }
    });
    onUpdate(map);
  }, (err) => {
    console.error('Firestore presence subscription error:', err);
  });
}

export function subscribeActivityFeed(onUpdate: (items: ActivityFeedItem[]) => void) {
  return onSnapshot(collection(db, FEED_COL), (snapshot) => {
    const items: ActivityFeedItem[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data() as ActivityFeedItem;
      if (data?.id) items.push(data);
    });
    items.sort((a, b) => {
      const aTime = typeof a.createdAt === 'number' ? a.createdAt : 0;
      const bTime = typeof b.createdAt === 'number' ? b.createdAt : 0;
      return bTime - aTime;
    });
    onUpdate(items);
  }, (err) => {
    console.error('Firestore activity feed subscription error:', err);
  });
}

export async function saveActivityFeedItemToFirestore(item: ActivityFeedItem): Promise<void> {
  try {
    await setDoc(doc(db, FEED_COL, item.id), sanitizeForFirestore(item), { merge: true });
  } catch (err) {
    console.error('Failed to save activity feed item to Firestore:', err);
  }
}

/**
 * Recursively strips `undefined` values from an object or array so Firestore `setDoc()` never fails.
 */
export function sanitizeForFirestore<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return null as unknown as T;
  }
  if (typeof obj !== 'object') {
    return obj;
  }
  if (obj instanceof Date) {
    return obj.toISOString() as unknown as T;
  }
  if (Array.isArray(obj)) {
    return obj
      .filter((item) => item !== undefined)
      .map((item) => sanitizeForFirestore(item)) as unknown as T;
  }
  const clean: Record<string, any> = {};
  for (const key of Object.keys(obj as any)) {
    const val = (obj as any)[key];
    if (val !== undefined) {
      clean[key] = sanitizeForFirestore(val);
    }
  }
  return clean as T;
}

export async function updatePresenceInFirestore(presence: PresenceDoc): Promise<void> {
  try {
    await setDoc(doc(db, PRESENCE_COL, presence.userId), sanitizeForFirestore(presence), { merge: true });
  } catch (err) {
    console.error('Failed to update presence in Firestore:', err);
  }
}

export async function clearThreadMessagesInFirestore(targetId: string, currentUserId: string): Promise<void> {
  try {
    const snaps = await getDocs(collection(db, MESSAGES_COL));
    for (const d of snaps.docs) {
      const msg = d.data() as ChatMessage;
      if (
        msg.senderId === targetId ||
        (msg.groupParticipants && msg.groupParticipants.includes(targetId))
      ) {
        await deleteDoc(doc(db, MESSAGES_COL, d.id));
      }
    }
    await setDoc(doc(db, 'archivedConversations', `${currentUserId}_${targetId}`), {
      userId: currentUserId,
      targetId,
      archivedAt: new Date().toISOString(),
    }, { merge: true });
  } catch (err) {
    console.error('Failed to clear thread messages in Firestore:', err);
  }
}

/**
 * Creates a clean, fresh rookie athlete profile without copying existing demo stats.
 */
export function createFreshAthleteProfile(params: {
  id: string;
  name: string;
  email: string;
  handle?: string;
  avatar?: string;
  primarySport?: SportType;
  role?: 'high_school' | 'college' | 'playground_pro' | 'scout_recruiter' | 'admin' | 'athlete';
  schoolOrLeague?: string;
  position?: string;
  jerseyNumber?: number;
  bio?: string;
  registeredCity?: string;
  registeredState?: string;
  hasCompletedLocationOnboarding?: boolean;
}): AthleteProfile {
  const formattedHandle = params.handle
    ? (params.handle.startsWith('@') ? params.handle : `@${params.handle.trim()}`)
    : `@${params.name.toLowerCase().replace(/[^a-z0-9]/g, '') || 'athlete'}`;

  return {
    id: params.id,
    name: params.name.trim() || 'New Athlete',
    handle: formattedHandle,
    email: params.email.trim(),
    avatar: params.avatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=300',
    role: params.role || 'playground_pro',
    schoolOrLeague: params.schoolOrLeague || 'Playground Rec League',
    primarySport: params.primarySport || 'basketball',
    position: params.position || 'All-Around',
    jerseyNumber: params.jerseyNumber || 7,
    height: `6'0"`,
    weight: '175 lbs',
    bio: params.bio || 'New registered athlete on Playgrounds. Ready to compete and log stats!',
    level: 1,
    xp: 0,
    xpToNextLevel: 1000,
    levelTitle: 'Rookie' as UserLevel,
    winCount: 0,
    lossCount: 0,
    winRate: 0,
    pointsPerGame: 0,
    reboundsPerGame: 0,
    assistsPerGame: 0,
    stealsPerGame: 0,
    blocksPerGame: 0,
    registeredMatchesCount: 0,
    statsHistory: [],
    badges: [],
    isPro: false,
    subscriptionTier: 'free',
    mfaEnabled: false,
    goals: [
      {
        id: `g1_${Date.now()}`,
        title: 'Daily Game Log',
        description: 'Record stats for at least 1 game played today.',
        target: 1,
        current: 0,
        unit: 'game',
        rewardXp: 150,
        type: 'daily',
        completed: false,
      },
      {
        id: `g2_${Date.now()}`,
        title: 'Park Check-In',
        description: 'Check in at a local court on the map.',
        target: 1,
        current: 0,
        unit: 'check-in',
        rewardXp: 200,
        type: 'daily',
        completed: false,
      }
    ],
    highlights: [],
    registeredCity: params.registeredCity || 'New York',
    registeredState: params.registeredState || 'NY',
    hasCompletedLocationOnboarding: params.hasCompletedLocationOnboarding ?? false,
    location: {
      lat: 40.7128,
      lng: -74.006,
      city: `${params.registeredCity || 'New York'}, ${params.registeredState || 'NY'}`,
      state: params.registeredState || 'NY',
    },
    stats: {
      basketball: { gamesPlayed: 0, pts: 0, ast: 0, reb: 0, stl: 0, blk: 0, fgMade: 0, fgAttempted: 0, threeMade: 0, threeAttempted: 0, ftMade: 0, ftAttempted: 0 },
      baseball: { gamesPlayed: 0, atBats: 0, hits: 0, runs: 0, rbis: 0, homeRuns: 0, stolenBases: 0, strikeouts: 0, walks: 0, battingAvg: 0, slugging: 0 },
      softball: { gamesPlayed: 0, atBats: 0, hits: 0, runs: 0, rbis: 0, homeRuns: 0, stolenBases: 0, battingAvg: 0 },
      pickleball: { gamesPlayed: 0, matchesWon: 0, matchesLost: 0, dinksCount: 0, acesCount: 0, winRate: 0 },
      soccer: { gamesPlayed: 0, goals: 0, assists: 0, shotsOnGoal: 0, tackles: 0, cleanSheets: 0, passAccuracy: 0 },
      volleyball: { gamesPlayed: 0, kills: 0, aces: 0, blocks: 0, digs: 0 },
      football: { gamesPlayed: 0, passingYards: 0, rushingYards: 0, touchdowns: 0, interceptions: 0 },
      tennis: { gamesPlayed: 0, aces: 0, breakPointsWon: 0, firstServePercentage: 0 },
    },
  };
}

// ==========================================
// REAL-TIME FIRESTORE SUBSCRIPTIONS
// ==========================================

export function subscribeAthletes(onUpdate: (athletes: AthleteProfile[]) => void) {
  return onSnapshot(collection(db, ATHLETES_COL), (snapshot) => {
    const list: AthleteProfile[] = [];
    snapshot.forEach((docSnap) => {
      list.push(docSnap.data() as AthleteProfile);
    });
    onUpdate(list);
  }, (err) => {
    console.error('Firestore athletes subscription error:', err);
  });
}

export function subscribePickupGames(onUpdate: (games: PickupGame[]) => void) {
  return onSnapshot(collection(db, GAMES_COL), (snapshot) => {
    const list: PickupGame[] = [];
    snapshot.forEach((docSnap) => {
      list.push(docSnap.data() as PickupGame);
    });
    onUpdate(list);
  }, (err) => {
    console.error('Firestore games subscription error:', err);
  });
}

export function subscribeMessages(
  onUpdate: (messages: ChatMessage[]) => void
) {
  const messagesQuery = query(
    collection(db, MESSAGES_COL),
    orderBy('createdAt', 'desc'),
    limit(100)
  );

  return onSnapshot(
    messagesQuery,
    (snapshot) => {
      const list: ChatMessage[] = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data() as ChatMessage;

        if (data && data.id) {
          list.push(data);
        }
      });

      const getMs = (m: ChatMessage): number => {
        if (typeof m.createdAt === 'number') return m.createdAt;

        if (typeof m.createdAt === 'string') {
          const t = new Date(m.createdAt).getTime();
          if (!isNaN(t)) return t;
        }

        if (m.timestamp) {
          const t = new Date(m.timestamp).getTime();
          if (!isNaN(t)) return t;
        }

        return 0;
      };

      list.sort((a, b) => getMs(a) - getMs(b));

      onUpdate(list);
    },
    (err) => {
      console.error('Firestore messages subscription error:', err);
    }
  );
}

/**
 * On-demand fetcher: Loads direct 1-on-1 messages between two specific athletes.
 * Only called when an athlete explicitly selects or starts a conversation with a 1-on-1 contact.
 */
export async function fetchDirectMessagesFromFirestore(userId1: string, userId2: string): Promise<ChatMessage[]> {
  try {
    const snaps = await getDocs(collection(db, MESSAGES_COL));
    const list: ChatMessage[] = [];
    snaps.forEach((d) => {
      const data = d.data() as ChatMessage;
      if (data && data.id) {
        const isDirectToTarget = (data.senderId === userId1 && data.recipientId === userId2);
        const isDirectFromTarget = (data.senderId === userId2 && data.recipientId === userId1);
        const isLegacyDirect = data.chatType === 'direct' && (
          (data.senderId === userId1 && data.recipientId === userId2) ||
          (data.senderId === userId2 && data.recipientId === userId1)
        );
        if (isDirectToTarget || isDirectFromTarget || isLegacyDirect) {
          list.push(data);
        }
      }
    });
    // Sort chronologically
    list.sort((a, b) => {
      const getMs = (m: ChatMessage): number => {
        if (typeof m.createdAt === 'number') return m.createdAt;
        if (typeof m.createdAt === 'string') {
          const t = new Date(m.createdAt).getTime();
          if (!isNaN(t)) return t;
        }
        if (m.timestamp) {
          const t = new Date(m.timestamp).getTime();
          if (!isNaN(t)) return t;
        }
        return 0;
      };
      return getMs(a) - getMs(b);
    });
    return list;
  } catch (err) {
    console.error(`Failed to fetch direct messages between ${userId1} and ${userId2}:`, err);
    return [];
  }
}

/**
 * Real-time subscription for 1-on-1 direct messages between two athletes.
 * Mounted on-demand when a user selects a specific 1-on-1 contact.
 */
export function subscribeDirectMessages(
  userId1: string,
  userId2: string,
  onUpdate: (messages: ChatMessage[]) => void
) {
  return onSnapshot(collection(db, MESSAGES_COL), (snapshot) => {
    const list: ChatMessage[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data() as ChatMessage;
      if (data && data.id) {

        const isDirectToTarget = data.senderId === userId1 && data.recipientId === userId2;
        const isDirectFromTarget = data.senderId === userId2 && data.recipientId === userId1;
        const isLegacyDirect =
          data.chatType === 'direct' &&
          ((data.senderId === userId1 && data.recipientId === userId2) ||
            (data.senderId === userId2 && data.recipientId === userId1));

        if (isDirectToTarget || isDirectFromTarget || isLegacyDirect) {
          list.push(data);
        }
      }
    });
    onUpdate(list);
  }, (err) => {
    console.error(`Firestore direct chat subscription error for ${userId1} and ${userId2}:`, err);
  });
}

/**
 * Lazy loading helper: Fetches public team and court channel messages on-demand.
 * Only called when the athlete explicitly opens or switches to the Team Chat tab.
 */
export async function fetchTeamChatMessagesFromFirestore(): Promise<ChatMessage[]> {
  try {
    const snaps = await getDocs(collection(db, MESSAGES_COL));
    const list: ChatMessage[] = [];
    snaps.forEach((d) => {
      const data = d.data() as ChatMessage;
      if (data && data.id) {
        // Filter for channel/group messages or public broadcast messages
        const isTeamMsg = !data.chatType || data.chatType === 'channel' || data.chatType === 'group' || !data.recipientId;
        if (isTeamMsg) {
          list.push(data);
        }
      }
    });
    // Sort chronologically
    list.sort((a, b) => {
      const getMs = (m: ChatMessage): number => {
        if (typeof m.createdAt === 'number') return m.createdAt;
        if (typeof m.createdAt === 'string') {
          const t = new Date(m.createdAt).getTime();
          if (!isNaN(t)) return t;
        }
        if (m.timestamp) {
          const t = new Date(m.timestamp).getTime();
          if (!isNaN(t)) return t;
        }
        return 0;
      };
      return getMs(a) - getMs(b);
    });
    return list;
  } catch (err) {
    console.error('Failed to lazy load team chat messages from Firestore:', err);
    return [];
  }
}

/**
 * Real-time subscription for team channel messages (lazy mounted on tab switch).
 */
export function subscribeTeamChatMessages(onUpdate: (messages: ChatMessage[]) => void) {
  return onSnapshot(collection(db, MESSAGES_COL), (snapshot) => {
    const list: ChatMessage[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data() as ChatMessage;
      if (data && data.id) {
        const isTeamMsg = !data.chatType || data.chatType === 'channel' || data.chatType === 'group' || !data.recipientId;
        if (isTeamMsg) {
          list.push(data);
        }
      }
    });
    list.sort((a, b) => {
      const getMs = (m: ChatMessage): number => {
        if (typeof m.createdAt === 'number') return m.createdAt;
        if (typeof m.createdAt === 'string') {
          const t = new Date(m.createdAt).getTime();
          if (!isNaN(t)) return t;
        }
        if (m.timestamp) {
          const t = new Date(m.timestamp).getTime();
          if (!isNaN(t)) return t;
        }
        return 0;
      };
      return getMs(a) - getMs(b);
    });
    onUpdate(list);
  }, (err) => {
    console.error('Firestore team chat subscription error:', err);
  });
}

export function subscribeCourts(onUpdate: (courts: CourtPOI[]) => void) {
  return onSnapshot(collection(db, COURTS_COL), (snapshot) => {
    const list: CourtPOI[] = [];
    snapshot.forEach((docSnap) => {
      list.push(docSnap.data() as CourtPOI);
    });
    onUpdate(list);
  }, (err) => {
    console.error('Firestore courts subscription error:', err);
  });
}

export function subscribeTournaments(onUpdate: (tournaments: Tournament[]) => void) {
  return onSnapshot(collection(db, TOURNAMENTS_COL), (snapshot) => {
    const list: Tournament[] = [];
    snapshot.forEach((docSnap) => {
      list.push(docSnap.data() as Tournament);
    });
    onUpdate(list);
  }, (err) => {
    console.error('Firestore tournaments subscription error:', err);
  });
}

export function subscribeNotifications(
  userId: string,
  onUpdate: (notifications: AppNotification[]) => void
) {
  const notificationsQuery = query(
    collection(db, NOTIFICATIONS_COL),
    where('recipientId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(50)
  );

  return onSnapshot(
    notificationsQuery,
    (snapshot) => {
      const list: AppNotification[] = [];

      snapshot.forEach((docSnap) => {
        list.push(docSnap.data() as AppNotification);
      });

      onUpdate(list);
    },
    (err) => {
      console.error('Firestore notifications subscription error:', err);
    }
  );
}

export interface HighlightInteractionDoc {
  id: string;
  likes?: number;
  views?: number;
  updatedAt?: string;
}

export function subscribeHighlights(onUpdate: (highlightsMap: Record<string, { likes: number; views: number }>) => void) {
  return onSnapshot(collection(db, HIGHLIGHTS_COL), (snapshot) => {
    const map: Record<string, { likes: number; views: number }> = {};
    snapshot.forEach((docSnap) => {
      const data = docSnap.data() as HighlightInteractionDoc;
      if (data && data.id) {
        map[data.id] = {
          likes: typeof data.likes === 'number' ? data.likes : 0,
          views: typeof data.views === 'number' ? data.views : 0,
        };
      }
    });
    onUpdate(map);
  }, (err) => {
    console.error('Firestore highlights subscription error:', err);
  });
}

// ==========================================
// INDIVIDUAL FIRESTORE SAVE / DELETE HELPERS
// ==========================================

export async function saveHighlightInteractionToFirestore(clipId: string, likes: number, views: number): Promise<void> {
  try {
    await setDoc(doc(db, HIGHLIGHTS_COL, clipId), sanitizeForFirestore({
      id: clipId,
      likes,
      views,
      updatedAt: new Date().toISOString(),
    }), { merge: true });
  } catch (err) {
    console.error('Failed to save highlight interaction to Firestore:', err);
  }
}

export async function saveNotificationToFirestore(notification: AppNotification): Promise<void> {
  try {
    await setDoc(doc(db, NOTIFICATIONS_COL, notification.id), sanitizeForFirestore(notification), { merge: true });
  } catch (err) {
    console.error('Failed to save notification to Firestore:', err);
  }
}

/**
 * Checks Firestore to verify if a pending friend request already exists between sender and recipient.
 */
export async function hasPendingFriendRequestInFirestore(senderId: string, recipientId: string): Promise<boolean> {
  try {
    const snaps = await getDocs(collection(db, NOTIFICATIONS_COL));
    for (const d of snaps.docs) {
      const data = d.data() as AppNotification;
      if (
        data &&
        data.type === 'friend_request' &&
        data.status === 'pending' &&
        ((data.senderId === senderId && data.recipientId === recipientId) ||
          (data.senderId === recipientId && data.recipientId === senderId))
      ) {
        return true;
      }
    }
    return false;
  } catch (err) {
    console.error('Failed to check pending friend request in Firestore:', err);
    return false;
  }
}

/**
 * Updates a message's delivery or read status in Firestore in real-time.
 */
export async function updateMessageStatusInFirestore(
  messageId: string,
  status: 'sent' | 'delivered' | 'read'
): Promise<void> {
  try {
    await setDoc(
      doc(db, MESSAGES_COL, messageId),
      sanitizeForFirestore({
        messageStatus: status,
        readStatus: status,
        updatedAt: Date.now(),
      }),
      { merge: true }
    );
  } catch (err) {
    console.error(`Failed to update message status to ${status} in Firestore:`, err);
  }
}

export async function deleteNotificationFromFirestore(notificationId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, NOTIFICATIONS_COL, notificationId));
  } catch (err) {
    console.error('Failed to delete notification from Firestore:', err);
  }
}

export async function clearAllNotificationsFromFirestore(): Promise<void> {
  try {
    const snaps = await getDocs(collection(db, NOTIFICATIONS_COL));
    for (const d of snaps.docs) {
      await deleteDoc(doc(db, NOTIFICATIONS_COL, d.id));
    }
  } catch (err) {
    console.error('Failed to clear all notifications from Firestore:', err);
  }
}

/**
 * Automated cleanup mechanism: Removes and archives notifications older than 7 days from Firestore.
 */
export async function cleanupOldNotificationsFromFirestore(maxAgeDays: number = 7): Promise<number> {
  try {
    const snaps = await getDocs(collection(db, NOTIFICATIONS_COL));
    const nowMs = Date.now();
    const maxAgeMs = maxAgeDays * 24 * 60 * 60 * 1000;
    let cleanedCount = 0;

    for (const d of snaps.docs) {
      const data = d.data() as AppNotification;
      let createdMs = 0;
      if (typeof data.createdAt === 'number') {
        createdMs = data.createdAt;
      } else if (typeof data.createdAt === 'string') {
        createdMs = new Date(data.createdAt).getTime();
      } else if (data.timestamp) {
        const parsed = new Date(data.timestamp).getTime();
        if (!isNaN(parsed)) createdMs = parsed;
      }

      if (createdMs > 0 && (nowMs - createdMs) > maxAgeMs) {
        await deleteDoc(doc(db, NOTIFICATIONS_COL, d.id));
        cleanedCount++;
      }
    }
    return cleanedCount;
  } catch (err) {
    console.error('Failed to cleanup old notifications from Firestore:', err);
    return 0;
  }
}

export async function saveAthleteToFirestore(athlete: AthleteProfile, options: { allowProDowngrade?: boolean } = {}): Promise<void> {
  try {
    const athleteRef = doc(db, ATHLETES_COL, athlete.id);
    const incomingData = sanitizeForFirestore(athlete);
    await runTransaction(db, async (transaction) => {
      const existingSnapshot = await transaction.get(athleteRef);
      const existingData = existingSnapshot.exists() ? existingSnapshot.data() : {};
      const existingIsPro = Boolean(
        existingData.isPro === true ||
        existingData.isVerifiedPro === true ||
        existingData.subscriptionTier === 'pro' ||
        existingData.role === 'playground_pro'
      );

      const dataToSave = existingIsPro && !options.allowProDowngrade
        ? {
          ...incomingData,
          isPro: true,
          isVerifiedPro: true,
          subscriptionTier: 'pro',
          role: existingData.role === 'admin' ? 'admin' : 'playground_pro',
        }
        : incomingData;

      transaction.set(athleteRef, dataToSave, { merge: true });
    });
  } catch (err) {
    console.error('Failed to save athlete to Firestore:', err);
  }
}

export async function deleteAthleteFromFirestore(athleteId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, ATHLETES_COL, athleteId));
  } catch (err) {
    console.error('Failed to delete athlete from Firestore:', err);
  }
}

export async function savePickupGameToFirestore(game: PickupGame): Promise<void> {
  try {
    await setDoc(doc(db, GAMES_COL, game.id), sanitizeForFirestore(game), { merge: true });
  } catch (err) {
    console.error('Failed to save pickup game to Firestore:', err);
  }
}

export async function saveMessageToFirestore(message: ChatMessage): Promise<void> {
  try {
    await setDoc(doc(db, MESSAGES_COL, message.id), sanitizeForFirestore(message), { merge: true });
  } catch (err) {
    console.error('Failed to save message to Firestore:', err);
  }
}

/**
 * Permanently deletes a single message from Firestore.
 */
export async function deleteChatMessageFromFirestore(messageId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, MESSAGES_COL, messageId));
  } catch (err) {
    console.error(`Failed to delete message ${messageId} from Firestore:`, err);
  }
}

/**
 * Permanently deletes all messages in a direct 1-on-1 conversation between two athletes from Firestore.
 */
export async function deleteDirectConversationFromFirestore(userId1: string, userId2: string): Promise<number> {
  try {
    const snaps = await getDocs(collection(db, MESSAGES_COL));
    let deletedCount = 0;
    const deletePromises: Promise<void>[] = [];

    snaps.forEach((d) => {
      const data = d.data() as ChatMessage;
      if (data) {
        const isDirectToTarget = (data.senderId === userId1 && data.recipientId === userId2);
        const isDirectFromTarget = (data.senderId === userId2 && (data.recipientId === userId1 || !data.recipientId));
        const isLegacyDirect = data.chatType === 'direct' && (
          (data.senderId === userId1 && data.recipientId === userId2) ||
          (data.senderId === userId2 && data.recipientId === userId1) ||
          (data.senderId === userId2 && !data.recipientId) ||
          (data.recipientId === userId2 && !data.senderId)
        );

        if (isDirectToTarget || isDirectFromTarget || isLegacyDirect) {
          deletePromises.push(deleteDoc(doc(db, MESSAGES_COL, d.id)));
          deletedCount++;
        }
      }
    });

    await Promise.all(deletePromises);
    return deletedCount;
  } catch (err) {
    console.error(`Failed to delete direct conversation from Firestore between ${userId1} and ${userId2}:`, err);
    return 0;
  }
}

/**
 * Permanently deletes all messages in a channel/group from Firestore.
 */
export async function deleteChannelConversationFromFirestore(channelId: string): Promise<number> {
  try {
    const snaps = await getDocs(collection(db, MESSAGES_COL));
    let deletedCount = 0;
    const deletePromises: Promise<void>[] = [];

    snaps.forEach((d) => {
      const data = d.data() as ChatMessage;
      if (data) {
        const isChannelMsg = data.gameId === channelId ||
          (channelId.startsWith('channel_') && (!data.chatType || data.chatType === 'channel' || data.chatType === 'group' || !data.recipientId));

        if (isChannelMsg) {
          deletePromises.push(deleteDoc(doc(db, MESSAGES_COL, d.id)));
          deletedCount++;
        }
      }
    });

    await Promise.all(deletePromises);
    return deletedCount;
  } catch (err) {
    console.error(`Failed to delete channel conversation from Firestore for channel ${channelId}:`, err);
    return 0;
  }
}

export async function saveCourtToFirestore(court: CourtPOI): Promise<void> {
  try {
    await setDoc(doc(db, COURTS_COL, court.id), sanitizeForFirestore(court), { merge: true });
  } catch (err) {
    console.error('Failed to save court to Firestore:', err);
  }
}

export async function saveTournamentToFirestore(tournament: Tournament): Promise<void> {
  try {
    await setDoc(doc(db, TOURNAMENTS_COL, tournament.id), sanitizeForFirestore(tournament), { merge: true });
  } catch (err) {
    console.error('Failed to save tournament to Firestore:', err);
  }
}

/**
 * Fetch or find an athlete profile from Firestore by UID or email.
 */
export async function getAthleteProfileFromFirestore(idOrEmail: string): Promise<AthleteProfile | null> {
  try {
    // Check by ID first
    const docRef = doc(db, ATHLETES_COL, idOrEmail);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      return snap.data() as AthleteProfile;
    }

    // Otherwise check all athletes by email
    const allSnaps = await getDocs(collection(db, ATHLETES_COL));
    let found: AthleteProfile | null = null;
    allSnaps.forEach((d) => {
      const data = d.data() as AthleteProfile;
      if (data.email?.toLowerCase() === idOrEmail.toLowerCase()) {
        found = data;
      }
    });
    return found;
  } catch (err) {
    console.error('Error fetching profile from Firestore:', err);
    return null;
  }
}

/**
 * Seed initial dataset if Firestore collections are completely empty
 */
export async function seedInitialFirestoreDataIfEmpty(): Promise<void> {
  try {
    // Data is created by authenticated users and admin workflows.
  } catch (err) {
    console.error('Error seeding initial Firestore data:', err);
  }
}

/**
 * Completely wipe Firestore collections for Admin Site Reset
 */
export async function wipeEntireFirestoreDatabase(): Promise<void> {
  try {
    // Delete all athletes
    const athletesSnap = await getDocs(collection(db, ATHLETES_COL));
    for (const d of athletesSnap.docs) {
      await deleteDoc(doc(db, ATHLETES_COL, d.id));
    }

    // Delete all games
    const gamesSnap = await getDocs(collection(db, GAMES_COL));
    for (const d of gamesSnap.docs) {
      await deleteDoc(doc(db, GAMES_COL, d.id));
    }

    // Delete all messages
    const msgsSnap = await getDocs(collection(db, MESSAGES_COL));
    for (const d of msgsSnap.docs) {
      await deleteDoc(doc(db, MESSAGES_COL, d.id));
    }

    const courtsSnap = await getDocs(collection(db, COURTS_COL));
    for (const d of courtsSnap.docs) {
      await deleteDoc(doc(db, COURTS_COL, d.id));
    }
  } catch (err) {
    console.error('Error wiping Firestore database:', err);
  }
}

/**
 * Subscribe to Auth Provider Settings in Firestore
 */
export function subscribeAuthProviderSettings(onUpdate: (settings: AuthProviderSettings) => void) {
  const docRef = doc(db, SETTINGS_COL, 'authProviderSettings');
  return onSnapshot(docRef, (snapshot) => {
    if (snapshot.exists()) {
      const data = snapshot.data() as AuthProviderSettings;
      onUpdate({
        googleEnabled: data.googleEnabled ?? false,
        facebookEnabled: data.facebookEnabled ?? false,
        appleEnabled: data.appleEnabled ?? false,
        strictRateLimitingEnabled: data.strictRateLimitingEnabled ?? false,
        providerOrder: Array.isArray(data.providerOrder) && data.providerOrder.length > 0
          ? data.providerOrder
          : ['google', 'facebook', 'apple'],
        allowedEmailDomains: Array.isArray(data.allowedEmailDomains) ? data.allowedEmailDomains : [],
      });
    } else {
      // Default initial state
      onUpdate({
        googleEnabled: false,
        facebookEnabled: false,
        appleEnabled: false,
        strictRateLimitingEnabled: false,
        providerOrder: ['google', 'facebook', 'apple'],
        allowedEmailDomains: [],
      });
    }
  }, (err) => {
    console.warn('Error subscribing to authProviderSettings:', err);
    onUpdate({
      googleEnabled: false,
      facebookEnabled: false,
      appleEnabled: false,
      strictRateLimitingEnabled: false,
      providerOrder: ['google', 'facebook', 'apple'],
      allowedEmailDomains: [],
    });
  });
}

/**
 * Save Auth Provider Settings to Firestore
 */
export async function saveAuthProviderSettingsToFirestore(settings: AuthProviderSettings): Promise<void> {
  try {
    const docRef = doc(db, SETTINGS_COL, 'authProviderSettings');
    await setDoc(docRef, sanitizeForFirestore(settings));
  } catch (err) {
    console.error('Error saving authProviderSettings to Firestore:', err);
  }
}

/**
 * Save UI Preferences to Firestore 'settings' collection
 */
export async function saveUIPreferencesToFirestore(uiPrefs: Record<string, any>): Promise<void> {
  try {
    const docRef = doc(db, SETTINGS_COL, 'uiPreferences');
    await setDoc(docRef, sanitizeForFirestore(uiPrefs));
  } catch (err) {
    console.error('Error saving uiPreferences to Firestore:', err);
  }
}

// ==========================================
// OUTGOING EMAIL DELIVERY LOGS HELPERS
// ==========================================
const EMAIL_LOGS_COL = 'email_logs';

export function subscribeEmailLogs(onUpdate: (logs: EmailDeliveryLog[]) => void): () => void {
  const q = query(collection(db, EMAIL_LOGS_COL), orderBy('timestamp', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const logs: EmailDeliveryLog[] = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data() as EmailDeliveryLog;
      if (data && data.id) {
        logs.push(data);
      }
    });
    onUpdate(logs);
  }, (err) => {
    console.warn('Error subscribing to Firestore email logs:', err);
    // Fallback: try querying without order if index is building
    getDocs(collection(db, EMAIL_LOGS_COL)).then((snap) => {
      const fallbackLogs: EmailDeliveryLog[] = [];
      snap.forEach((d) => {
        const data = d.data() as EmailDeliveryLog;
        if (data && data.id) fallbackLogs.push(data);
      });
      fallbackLogs.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      onUpdate(fallbackLogs);
    }).catch(() => { });
  });
}

export async function saveEmailLogToFirestore(log: EmailDeliveryLog): Promise<void> {
  try {
    await setDoc(doc(db, EMAIL_LOGS_COL, log.id), sanitizeForFirestore(log), { merge: true });
  } catch (err) {
    console.error('Failed to save email log to Firestore:', err);
  }
}

export async function deleteEmailLogFromFirestore(logId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, EMAIL_LOGS_COL, logId));
  } catch (err) {
    console.error('Failed to delete email log from Firestore:', err);
  }
}

export async function clearAllEmailLogsFromFirestore(): Promise<void> {
  try {
    const snapshot = await getDocs(collection(db, EMAIL_LOGS_COL));
    const batch = writeBatch(db);
    snapshot.docs.forEach((d) => {
      batch.delete(d.ref);
    });
    await batch.commit();
  } catch (err) {
    console.error('Failed to clear all email logs from Firestore:', err);
  }
}

// ==========================================
// EMAIL TEMPLATE PERSISTENCE
// ==========================================
export function subscribeEmailTemplate(onUpdate: (templateHtml: string | null) => void): () => void {
  const docRef = doc(db, SETTINGS_COL, 'email_template');
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();
      onUpdate(data?.templateHtml || null);
    } else {
      onUpdate(null);
    }
  }, (err) => {
    console.warn('Error reading custom email template from Firestore:', err);
    onUpdate(null);
  });
}

export async function saveEmailTemplateToFirestore(templateHtml: string | null): Promise<void> {
  try {
    const docRef = doc(db, SETTINGS_COL, 'email_template');
    if (templateHtml === null) {
      await deleteDoc(docRef).catch(() => { });
    } else {
      await setDoc(docRef, sanitizeForFirestore({
        templateHtml,
        updatedAt: Date.now(),
      }));
    }
  } catch (err) {
    console.error('Failed to save email template to Firestore:', err);
  }
}


